import { useEffect, useState } from 'react';
import axios from 'axios';
import { Plus } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '../ui/button';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '../ui/dialog';
import SiteFields, { emptySite } from './SiteFields';
import useCsmCatalogue from '../../hooks/useCsmCatalogue';
import { siteError } from '../../utils/siteLocation';
import { extractErrorMessage } from '../../utils/apiErrors';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const AddSiteLocationDialog = ({ siteName, customerId, onClose, onAdded }) => {
  const [draft, setDraft] = useState(emptySite());
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const csm = useCsmCatalogue();
  useEffect(() => { if (siteName) { setDraft({ ...emptySite(), name: siteName }); setError(''); } }, [siteName]);
  const hasLocation = Boolean(draft.lsd.trim() || draft.address.trim());
  const invalid = siteError(draft, csm.lsdLayout(draft.province));
  const save = async (e) => {
    e.preventDefault(); if (saving || !hasLocation || invalid) return;
    setSaving(true); setError('');
    try {
      const { lsd, address, province, components } = draft;
      const res = await axios.post(`${API}/customers/${customerId}/sites/locations`, { site_name: siteName, lsd, address, province, components });
      onAdded(res.data); onClose(); toast.success('Location added to site');
    } catch (err) { setError(extractErrorMessage(err, 'Unable to add the location')); }
    finally { setSaving(false); }
  };
  return (
    <Dialog open={Boolean(siteName)} onOpenChange={(open) => !open && !saving && onClose()}>
      <DialogContent className="grid-cols-[minmax(0,1fr)] sm:max-w-xl max-h-[90dvh] overflow-y-auto" data-testid="site-location-dialog">
        <DialogHeader><DialogTitle>Add new LSD/address</DialogTitle><DialogDescription className="break-words" data-testid="site-location-parent-name">{siteName}</DialogDescription></DialogHeader>
        <form onSubmit={save} className="space-y-4">
          <SiteFields nameReadOnly value={draft} onChange={(value) => { setDraft(value); setError(''); }} testPrefix="site-location-new" />
          {error && <p role="alert" className="text-sm text-red-600" data-testid="site-location-error">{error}</p>}
          <div className="flex flex-wrap justify-end gap-2">
            <Button type="button" variant="outline" disabled={saving} onClick={onClose} data-testid="site-location-cancel">Cancel</Button>
            <Button type="submit" disabled={saving || !hasLocation || !!invalid} data-testid="site-location-save"><Plus size={14} className="mr-1.5" />{saving ? 'Saving...' : 'Add location'}</Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};